export type ApiValidationErrors = Record<string, string[] | undefined>;
export type TranslatedApiValidationErrors = Record<string, string[]>;

const FIELD_LABELS: Record<string, string> = {
  acepta_terminos: 'la aceptación de términos',
  acepta_condiciones: 'la aceptación de condiciones',
  active: 'el estado',
  apellido: 'el apellido',
  apellidos: 'los apellidos',
  archivo: 'el archivo',
  company_id: 'la empresa',
  categoria: 'la categoría',
  categoria_id: 'la categoría',
  categorias_uso: 'la categoría de uso',
  categoria_uso_otra: 'la categoría de uso (otra)',
  clientes_hijas_ids: 'las empresas hijas',
  contenido: 'el contenido',
  current_password: 'la contraseña actual',
  descripcion: 'la descripción',
  direccion: 'la dirección',
  email: 'el email',
  email_contacto: 'el email de contacto',
  email_notificaciones_internas: 'el email de notificaciones internas',
  email_remitente: 'el email remitente',
  estado: 'el estado',
  expiracion_sesion_minutos: 'la expiración de sesión',
  fecha_limite: 'la fecha límite',
  firstName: 'el nombre',
  lastName: 'los apellidos',
  medidas: 'las medidas',
  medidas_adicionales: 'las medidas adicionales',
  message: 'el mensaje',
  name: 'el nombre',
  nif: 'el NIF',
  nombre: 'el nombre',
  nombre_empresa: 'el nombre de la empresa',
  nombre_remitente: 'el nombre remitente',
  numero_empleados: 'el número de empleados',
  password: 'la contraseña',
  password_confirmation: 'la confirmación de contraseña',
  phone: 'el teléfono',
  plan: 'el plan',
  pregunta: 'la pregunta',
  respuesta: 'la respuesta',
  revision_humana: 'la revisión humana',
  sedes: 'las sedes',
  telefono: 'el teléfono',
  texto: 'el texto',
  tipo_respuesta: 'el tipo de respuesta',
  titulo: 'el título',
};

const DIRECT_MESSAGES: Record<string, string> = {
  'Unauthenticated.': 'Sesión no autenticada.',
  'Forbidden.': 'No tienes permisos para realizar esta acción.',
  'This action is unauthorized.': 'No tienes permisos para realizar esta acción.',
  'Not Found.': 'No se encontró el recurso solicitado.',
  'Too Many Attempts.': 'Demasiados intentos. Inténtalo de nuevo más tarde.',
  'The given data was invalid.': 'Los datos introducidos no son válidos.',
  'The current password is incorrect.': 'La contraseña actual es incorrecta.',
  'The provided password does not match your current password.': 'La contraseña indicada no coincide con tu contraseña actual.',
  'These credentials do not match our records.': 'Las credenciales no coinciden con nuestros registros.',
};

export function translateApiMessage(message: string | null | undefined): string {
  if (!message) return '';

  const trimmed = message.trim();
  if (!trimmed) return '';
  if (DIRECT_MESSAGES[trimmed]) return DIRECT_MESSAGES[trimmed];

  const translated = translateLaravelValidationMessage(trimmed);
  return translated ?? trimmed;
}

export function translateApiValidationErrors(errors: ApiValidationErrors): TranslatedApiValidationErrors {
  return Object.fromEntries(
    Object.entries(errors).map(([field, messages]) => [
      field,
      (messages ?? []).map(message => translateApiMessage(message)),
    ]),
  );
}

export function firstTranslatedValidationMessage(errors: ApiValidationErrors): string | null {
  for (const messages of Object.values(errors)) {
    const first = messages?.[0];
    if (first) return translateApiMessage(first);
  }
  return null;
}

function translateLaravelValidationMessage(message: string): string | null {
  const normalized = message.replace(/\s+/g, ' ');

  const required = normalized.match(/^The (.+) field is required\.$/i);
  if (required) return `${capitalize(fieldLabel(required[1]))} es obligatorio.`;

  const minChars = normalized.match(/^The (.+) field must be at least (\d+) characters\.$/i);
  if (minChars) return `${capitalize(fieldLabel(minChars[1]))} debe tener al menos ${minChars[2]} caracteres.`;

  const maxChars = normalized.match(/^The (.+) field must not be greater than (\d+) characters\.$/i);
  if (maxChars) return `${capitalize(fieldLabel(maxChars[1]))} no puede superar los ${maxChars[2]} caracteres.`;

  const exactChars = normalized.match(/^The (.+) field must be (\d+) characters\.$/i);
  if (exactChars) return `${capitalize(fieldLabel(exactChars[1]))} debe tener ${exactChars[2]} caracteres.`;

  const minNumber = normalized.match(/^The (.+) field must be at least (\d+)\.$/i);
  if (minNumber) return `${capitalize(fieldLabel(minNumber[1]))} debe ser como mínimo ${minNumber[2]}.`;

  const maxNumber = normalized.match(/^The (.+) field must not be greater than (\d+)\.$/i);
  if (maxNumber) return `${capitalize(fieldLabel(maxNumber[1]))} no puede ser mayor que ${maxNumber[2]}.`;

  const email = normalized.match(/^The (.+) field must be a valid email address\.$/i);
  if (email) return `${capitalize(fieldLabel(email[1]))} debe ser un email válido.`;

  const invalidFormat = normalized.match(/^The (.+) field format is invalid\.$/i);
  if (invalidFormat) return `El formato de ${stripArticle(fieldLabel(invalidFormat[1]))} no es válido.`;

  const selected = normalized.match(/^The selected (.+) is invalid\.$/i);
  if (selected) return `${capitalize(fieldLabel(selected[1]))} seleccionado no es válido.`;

  const unique = normalized.match(/^The (.+) has already been taken\.$/i);
  if (unique) return `${capitalize(fieldLabel(unique[1]))} ya está en uso.`;

  const confirmed = normalized.match(/^The (.+) field confirmation does not match\.$/i);
  if (confirmed) return `La confirmación de ${stripArticle(fieldLabel(confirmed[1]))} no coincide.`;

  const string = normalized.match(/^The (.+) field must be a string\.$/i);
  if (string) return `${capitalize(fieldLabel(string[1]))} debe ser texto.`;

  const integer = normalized.match(/^The (.+) field must be an integer\.$/i);
  if (integer) return `${capitalize(fieldLabel(integer[1]))} debe ser un número entero.`;

  const numeric = normalized.match(/^The (.+) field must be a number\.$/i);
  if (numeric) return `${capitalize(fieldLabel(numeric[1]))} debe ser un número.`;

  const array = normalized.match(/^The (.+) field must be an array\.$/i);
  if (array) return `${capitalize(fieldLabel(array[1]))} debe ser una lista.`;

  const boolean = normalized.match(/^The (.+) field must be true or false\.$/i);
  if (boolean) return `${capitalize(fieldLabel(boolean[1]))} debe ser verdadero o falso.`;

  const date = normalized.match(/^The (.+) field must be a valid date\.$/i);
  if (date) return `${capitalize(fieldLabel(date[1]))} debe ser una fecha válida.`;

  const accepted = normalized.match(/^The (.+) field must be accepted\.$/i);
  if (accepted) return `${capitalize(fieldLabel(accepted[1]))} debe aceptarse.`;

  const uploaded = normalized.match(/^The (.+) failed to upload\.$/i);
  if (uploaded) return `No se pudo subir ${stripArticle(fieldLabel(uploaded[1]))}.`;

  const file = normalized.match(/^The (.+) field must be a file\.$/i);
  if (file) return `${capitalize(fieldLabel(file[1]))} debe ser un archivo.`;

  const oneOf = normalized.match(/^The (.+) field must be one of: (.+)\.$/i);
  if (oneOf) return `${capitalize(fieldLabel(oneOf[1]))} debe ser una de estas opciones: ${oneOf[2]}.`;

  const passwordLetters = normalized.match(/^The (.+) field must contain at least one uppercase and one lowercase letter\.$/i);
  if (passwordLetters) return `${capitalize(fieldLabel(passwordLetters[1]))} debe incluir al menos una mayúscula y una minúscula.`;

  const passwordNumber = normalized.match(/^The (.+) field must contain at least one number\.$/i);
  if (passwordNumber) return `${capitalize(fieldLabel(passwordNumber[1]))} debe incluir al menos un número.`;

  const passwordSymbol = normalized.match(/^The (.+) field must contain at least one symbol\.$/i);
  if (passwordSymbol) return `${capitalize(fieldLabel(passwordSymbol[1]))} debe incluir al menos un símbolo.`;

  return null;
}

function fieldLabel(rawField: string): string {
  const parts = rawField
    .replace(/\.\d+\./g, '.')
    .replace(/\.\d+$/g, '')
    .replace(/\[\]/g, '')
    .split('.');
  const clean = (parts[parts.length - 1] ?? rawField).trim();

  return FIELD_LABELS[clean] ?? `el campo ${clean.replace(/_/g, ' ')}`;
}

function capitalize(value: string): string {
  return value.charAt(0).toUpperCase() + value.slice(1);
}

function stripArticle(value: string): string {
  return value.replace(/^(el|la|los|las)\s+/i, '');
}
